import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import { $getRoot, TextNode } from "lexical";
import { useEffect, useRef } from "react";
import { useLexicalContext } from "../lexical-context";
import {
  $createNonGsmHighlightNode,
  $isNonGsmHighlightNode,
  NonGsmHighlightNode,
} from "../nodes/non-gsm-highlight-node";
import { segmentByGsmStatus } from "../utils/gsm-encoding";

import type { GsmSegment } from "../utils/gsm-encoding";

/**
 * The offsets at which a node must be split so each part holds one segment.
 * The final segment is the implicit remainder, so it contributes no offset.
 */
const splitOffsets = (segments: GsmSegment[]): number[] => {
  const offsets: number[] = [];
  let offset = 0;

  for (const segment of segments.slice(0, -1)) {
    offset += segment.text.length;
    offsets.push(offset);
  }

  return offsets;
};

/**
 * Swap `node` for the node type that matches `isGsm`, carrying over its text
 * and format. A no-op when the node is already the right type, which is what
 * keeps the transforms from looping.
 */
const $matchNodeToGsmStatus = (node: TextNode, isGsm: boolean): void => {
  if (isGsm !== $isNonGsmHighlightNode(node)) return;

  const text = node.getTextContent();
  const replacement = isGsm ? new TextNode(text) : $createNonGsmHighlightNode(text);
  replacement.setFormat(node.getFormat());
  node.replace(replacement);
};

const $syncGsmHighlighting = (node: TextNode): void => {
  const segments = segmentByGsmStatus(node.getTextContent());
  const [firstSegment] = segments;

  if (!firstSegment) {
    $matchNodeToGsmStatus(node, true);
    return;
  }

  if (segments.length === 1) {
    $matchNodeToGsmStatus(node, firstSegment.isGsm);
    return;
  }

  // splitText carries the user's selection across the split so the caret stays
  // put; each resulting part then takes the type its content requires.
  node.splitText(...splitOffsets(segments)).forEach((part, index) => {
    const segment = segments[index];
    if (segment) $matchNodeToGsmStatus(part, segment.isGsm);
  });
};

/**
 * Highlights the characters Twilio cannot encode as GSM-7, since those force
 * the message into UCS-2 and shrink the per-segment character allowance.
 */
export const NonGsmHighlightPlugin = (): null => {
  const [editor] = useLexicalComposerContext();
  const {
    text: { nonGsmHighlightVisible },
  } = useLexicalContext();

  useEffect(() => {
    const transform = (node: TextNode): void => {
      if (nonGsmHighlightVisible) {
        $syncGsmHighlighting(node);
      } else if ($isNonGsmHighlightNode(node)) {
        // Highlighting is off, so strip the highlight but leave the text.
        $matchNodeToGsmStatus(node, true);
      }
    };

    return mergeRegister(
      editor.registerNodeTransform(TextNode, transform),
      editor.registerNodeTransform(NonGsmHighlightNode, transform),
    );
  }, [editor, nonGsmHighlightVisible]);

  // Transforms only run for nodes that have changed, so flipping the toggle
  // has to dirty the existing content for it to pick up the new setting.
  const wasVisible = useRef(nonGsmHighlightVisible);
  useEffect(() => {
    if (wasVisible.current === nonGsmHighlightVisible) return;
    wasVisible.current = nonGsmHighlightVisible;

    editor.update(() => {
      for (const node of $getRoot().getAllTextNodes()) {
        if (node.getType() === "text" || $isNonGsmHighlightNode(node)) node.markDirty();
      }
    });
  }, [editor, nonGsmHighlightVisible]);

  return null;
};
